import React, {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { throttle } from 'lodash'
import { jwtDecode } from 'jwt-decode'

import {
  CIAM_SESSION_TOKEN_STORAGE_KEY,
  CIAM_ID_TOKEN_STORAGE_KEY,
  CIAM_ID_TOKEN_UPDATED_EVENT,
  fetchCiamUserProfile,
  getCiamSessionToken,
  syncCiamEmailCookie,
} from './hooks/useCiamAuthorize'

const store = createContext()

const AuthHydrationContext = createContext(false)

// Id token JWT → payload (no signature verification).
const decodeCiamIdTokenJwt = token => {
  if (typeof token !== 'string' || token === '') {
    return null
  }
  try {
    return jwtDecode(token)
  } catch {
    return null
  }
}

// Session `CiamIdToken` string → claims object (JWT first, else legacy JSON payload).
const parseStoredCiamIdToken = raw => {
  if (raw == null || raw === '') {
    return null
  }
  const fromJwt = decodeCiamIdTokenJwt(raw)
  if (fromJwt != null) {
    return fromJwt
  }
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// `p1.userId` is a single claim name on the id token (not nested under `p1`).
const getCiamUserIdFromIdTokenClaims = decoded => {
  if (decoded == null) {
    return null
  }
  const v = decoded['p1.userId']
  if (v == null || String(v) === '') {
    return null
  }
  return String(v)
}

const getHasSessionTokenFromSessionStorage = () => {
  if (typeof window === 'undefined') {
    return false
  }
  const token = sessionStorage.getItem(CIAM_SESSION_TOKEN_STORAGE_KEY)
  return token != null && token !== ''
}

const { Provider } = store

const getParameterByName = name => {
  if (typeof window === 'undefined') return
  let url = window.location.href
  name = name.replace(/[[\]]/g, '\\$&')
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

const initialState = {
  borrower: {
    ReferenceID: '',
    Originator: 'Borrower',
    SourceLinkedFrom: getParameterByName('s') || '',
    NoRef_Kind: '',
    NoRef_Other: false,
    MustAttachProofOfInsurance: 'false',
    IsAgent: false,
  },
  userData: null,
  ciamUserId: null,
  form: null,
  hasSessionToken: false,
  IsAccountDeleted: false,
  TriggeredLogout: false,
  hasSignoutError: false,
}

const readCiamSliceFromSessionStorage = () => {
  if (typeof window === 'undefined') {
    return { hasSessionToken: false, ciamUserId: null }
  }
  const rawId = sessionStorage.getItem(CIAM_ID_TOKEN_STORAGE_KEY)
  const decoded = parseStoredCiamIdToken(rawId)
  return {
    hasSessionToken: getHasSessionTokenFromSessionStorage(),
    ciamUserId: getCiamUserIdFromIdTokenClaims(decoded),
  }
}

const loadState = initialState => {
  if (typeof window === 'undefined') {
    return initialState
  }
  try {
    const serializedState = sessionStorage.getItem('state')
    const ciam = readCiamSliceFromSessionStorage()

    if (serializedState === null) {
      return { ...initialState, ...ciam }
    }

    const deserializedState = JSON.parse(serializedState)

    const source = getParameterByName('s')
    if (source) {
      deserializedState.borrower.SourceLinkedFrom = source
    }

    return {
      ...deserializedState,
      hasSessionToken: ciam.hasSessionToken,
      userData: deserializedState.userData,
      ciamUserId: ciam.ciamUserId,
    }
  } catch (error) {
    return initialState
  }
}

const saveState = state => {
  try {
    const serializedState = JSON.stringify(state)
    sessionStorage.setItem('state', serializedState)
  } catch (error) {
    // Ignore write errors.
  }
}

const reducer = (state, action) => {
  const { type, payload } = action

  switch (type) {
    case 'addToState': {
      return {
        ...state,
        ...payload,
      }
    }
    case 'resetForm': {
      return {
        ...initialState,
      }
    }
    default:
      throw new Error('bad action type')
  }
}

const StateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState, loadState)
  const [authHydrationReady, setAuthHydrationReady] = useState(false)

  useLayoutEffect(() => {
    setAuthHydrationReady(true)
  }, [])

  console.count('StateProvider render')

  useEffect(() => {
    let cancelled = false
    let syncGeneration = 0

    const syncCiamUserDataFromSession = async () => {
      if (typeof window === 'undefined') {
        return
      }
      const generation = ++syncGeneration
      const raw = sessionStorage.getItem(CIAM_ID_TOKEN_STORAGE_KEY)
      const decoded = parseStoredCiamIdToken(raw)
      const hasSessionToken = getHasSessionTokenFromSessionStorage()
      const sessionToken = getCiamSessionToken() ?? ''

      const ciamUserId = getCiamUserIdFromIdTokenClaims(decoded)

      let userData = null
      if (ciamUserId != null && sessionToken !== '') {
        const refIdStored = localStorage.getItem('refId')
        if (refIdStored) {
          try {
            const patchRes = await fetch(
              `${process.env.GATSBY_API_URL}/ciam/user/${encodeURIComponent(
                ciamUserId
              )}`,
              {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${sessionToken}`,
                },
                body: JSON.stringify({
                  RefId: { Add: [refIdStored] },
                }),
              }
            )
            if (patchRes.ok) {
              localStorage.removeItem('refId')
            } else {
              console.log(
                new Error(
                  `CIAM PATCH RefId failed: ${patchRes.status} ${patchRes.statusText}`
                )
              )
            }
          } catch (error) {
            console.log(error)
          }
        }

        if (cancelled || generation !== syncGeneration) {
          return
        }

        try {
          userData = await fetchCiamUserProfile(ciamUserId, sessionToken)
        } catch (error) {
          console.error('Error calling CIAM GET /ciam/user:', error)
        }
      }

      if (cancelled || generation !== syncGeneration) {
        return
      }

      syncCiamEmailCookie(userData?.Email)

      console.log('userData (processed after CIAM sync)', userData)
      dispatch({
        type: 'addToState',
        payload: {
          userData,
          hasSessionToken,
          ciamUserId,
        },
      })
    }

    void syncCiamUserDataFromSession()
    const onIdTokenUpdated = () => {
      void syncCiamUserDataFromSession()
    }
    window.addEventListener(CIAM_ID_TOKEN_UPDATED_EVENT, onIdTokenUpdated)
    return () => {
      cancelled = true
      syncGeneration += 1
      window.removeEventListener(CIAM_ID_TOKEN_UPDATED_EVENT, onIdTokenUpdated)
    }
  }, [dispatch])

  // only save state to sessionStorage at most once per second
  // (serialization is CPU $$$)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledSaveState = useCallback(
    throttle(state => saveState(state), 1000),
    []
  )

  // memoize aka cache state and dispatch separately for performance gains
  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch])

  // watch for state changes and write it to sessionStorage
  useEffect(
    () => {
      if (
        state.borrower?.PolicyholderAddress1 &&
        !state.borrower?.FullAddress
      ) {
        dispatch({
          type: 'addToState',
          payload: {
            borrower: {
              ...state.borrower,

              // prettier-ignore
              FullAddress: `${state.borrower.PolicyholderAddress1 + (state.borrower.PolicyholderAddress2 ? ` ${state.borrower.PolicyholderAddress2},` : `,`)} ${state.borrower.PolicyholderCity}, ${state.borrower.PolicyholderState} ${state.borrower.PolicyholderZip}`,
            },
          },
        })
      }
      throttledSaveState(state)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  )

  // set originator to agent if IsAgent is true
  useEffect(() => {
    if (state.form?.IsAgent) {
      dispatch({
        type: 'addToState',
        payload: { form: { ...state.form, Originator: 'Agent' } },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.form?.IsAgent])

  return (
    <Provider value={contextValue}>
      <AuthHydrationContext.Provider value={authHydrationReady}>
        {children}
      </AuthHydrationContext.Provider>
    </Provider>
  )
}

export { store, StateProvider, AuthHydrationContext }
